import React, { useEffect, useState, useCallback } from 'react';
import { Tabs, Select, DatePicker, Input, InputNumber, Modal, Spin, Radio, message } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { usePWG201Mode } from './hooks/usePWG201Mode';
import { usePWG201Record } from './hooks/usePWG201Record';
import { usePWG201Navigation } from './hooks/usePWG201Navigation';
import DetailsTab from './components/DetailsTab';
import WriteoffAdvTab from './components/WriteoffAdvTab';
import WriteoffBorrTab from './components/WriteoffBorrTab';
import WriteoffAdv2Tab from './components/WriteoffAdv2Tab';
import AllocFeeTab from './components/AllocFeeTab';
import ApprovalHistoryModal from './components/ApprovalHistoryModal';
import { useModulePermission } from '../../hooks/usePermission';
import { SharedActionBar } from '../../components/SharedActionBar';
import LookupInput from '../../components/LookupInput';
import { FG110M, pwg201Api } from '../../services/pwg201Api';
import { auditLogApi } from '../../services/auditLogApi';
import {
  AcceptanceDataPanel,
  AcceptanceFieldHint,
  AcceptanceToolbar,
  pwg201f1AcceptanceManifest,
  useAcceptanceMode,
} from '../../features/acceptance';
import './PWG201Form.css';

const PAY_TYPE_OPTIONS = [
  { value: '1', label: '貨款' },
  { value: '5', label: '預收款' },
  { value: '6', label: '營業外收入' },
  { value: '8', label: '匯入款' },
  { value: '9', label: '還款' },
];

const PAY_METHOD_OPTIONS = [
  { value: '01', label: '現金' },
  { value: '02', label: '押匯' },
  { value: '03', label: '支票' },
  { value: '04', label: '電匯' },
  { value: '05', label: '轉帳' },
  { value: '07', label: '沖預收款' },
  { value: '09', label: '分公司往來' },
  { value: '10', label: '應收代收款' },
];

const ITEM_CAT_OPTIONS = [
  { value: '08', label: '銷貨收入' },
  { value: '09', label: '樣品收入' },
  { value: '10', label: '佣金收入' },
  { value: '11', label: '利息收入' },
  { value: '13', label: '其它收入' },
  { value: '14', label: '沖暫借款' },
  { value: '19', label: '沖代墊款' },
  { value: '20', label: '其它應收款' },
  { value: '21', label: '代收昆山款' },
];

const SC_NO_TYPE_OPTIONS = [
  { value: 1, label: '有S/C NO.' },
  { value: 2, label: '無S/C NO.' },
];

const PWG201Form: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reviewId = searchParams.get('review');
  const { mode, isEditing, startAdd, startEdit, exitEdit } = usePWG201Mode();
  const permissions = useModulePermission('pwg201f1');

  const {
    record, lastApiResponse, formData, details, writeoffAdv, writeoffBorr, writeoffAdv2, allocFee,
    loading, validationErrors,
    loadRecord, resetForAdd, loadAcceptanceDraft, revertToRecord, handleSave, handleDelete,
    getField, setField,
    setDetails, setWriteoffAdv, setWriteoffBorr, setWriteoffAdv2, setAllocFee,
  } = usePWG201Record();

  const loadAndExit = useCallback(async (id: string) => {
    await loadRecord(id);
  }, [loadRecord]);

  const {
    allIds, currentIdx, loadList, navFirst, navPrev, navNext, navLast,
    addIdToList, removeIdFromList,
  } = usePWG201Navigation(loadAndExit);

  const [activeTab, setActiveTab] = useState('details');
  const [showHistory, setShowHistory] = useState(false);
  const [acceptanceLatest, setAcceptanceLatest] = useState<FG110M[]>([]);
  const [acceptanceAudit, setAcceptanceAudit] = useState<unknown[]>([]);
  const [acceptanceDatabaseRecord, setAcceptanceDatabaseRecord] = useState<FG110M | null>(null);
  const [acceptanceErrors, setAcceptanceErrors] = useState<string[]>([]);
  const [acceptanceLoading, setAcceptanceLoading] = useState(false);
  const acceptanceMode = useAcceptanceMode();
  const acceptanceEnabled = acceptanceMode.isEnabled(pwg201f1AcceptanceManifest.formId);

  useEffect(() => {
    if (!permissions.canView) return;
    loadList().then(() => {
      if (reviewId) {
        loadAndExit(reviewId);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions.canView]);

  // Approval state derived from fg110_80
  // 狀態統一碼（規格法定值）：N=草稿/已沖銷、D=送審中、P=已核准（自創碼 S/R/V 已廢除）
  const handleAdd = () => {
    resetForAdd();
    startAdd();
  };

  const handleEdit = () => {
    startEdit();
  };

  const handleSaveClick = () => {
    handleSave(
      (id) => { addIdToList(id); },
      exitEdit
    );
  };

  const handleRevert = () => {
    revertToRecord();
    exitEdit();
  };

  const handleDeleteClick = () => {
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除繳款單 ${record?.fg110_01} 嗎？`,
      okType: 'danger',
      onOk: () => {
        handleDelete(() => {
          removeIdFromList();
          exitEdit();
          if (allIds.length > 1) loadList();
        });
      },
    });
  };

  const handleSubmit = () => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認送審',
      content: `確定要將繳款單 ${record.fg110_01} 送審？`,
      okText: '送審', cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await pwg201Api.submitForApproval(record.fg110_01);
          message.success(resp.message || '送審成功');
          await loadRecord(record.fg110_01);
        } catch (err: any) {
          message.error(err?.response?.data?.error || '送審失敗');
        }
      },
    });
  };

  const handleApprove = () => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認核准',
      content: `確定要核准繳款單 ${record.fg110_01}？`,
      okText: '核准', cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await pwg201Api.approve(record.fg110_01);
          message.success(resp.message || '核准成功');
          await loadRecord(record.fg110_01);
        } catch (err: any) {
          message.error(err?.response?.data?.error || '核准失敗');
        }
      },
    });
  };

  const handleReject = () => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認退回',
      content: `確定要退回繳款單 ${record.fg110_01}？`,
      okText: '退回', okType: 'danger', cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await pwg201Api.reject(record.fg110_01);
          message.success(resp.message || '退回成功');
          await loadRecord(record.fg110_01);
        } catch (err: any) {
          message.error(err?.response?.data?.error || '退回失敗');
        }
      },
    });
  };

  const handleWriteoff = () => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷此單據 ${record.fg110_01}？沖銷後可重新送審。`,
      okText: '沖銷', okType: 'danger', cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await pwg201Api.writeoff(record.fg110_01);
          message.success(resp.message || '沖銷成功');
          await loadRecord(record.fg110_01);
        } catch (err: any) {
          message.error(err?.response?.data?.error || '沖銷失敗');
        }
      },
    });
  };

  // Legacy callbacks are intentionally unreachable while old client bundles drain;
  // no action bar prop exposes them and the server no longer registers the routes.
  void handleSubmit;
  void handleApprove;
  void handleReject;
  void handleWriteoff;

  const loadAcceptanceData = useCallback(async () => {
    if (!acceptanceEnabled) return;
    setAcceptanceLoading(true);
    const [latest, database, audit] = await Promise.allSettled([
      pwg201Api.list(1, 3),
      record?.fg110_01 ? pwg201Api.get(record.fg110_01) : Promise.resolve(null),
      auditLogApi.list({
        form_id: pwg201f1AcceptanceManifest.formId,
        doc_key: record?.fg110_01,
        page: 1,
        pageSize: 20,
      }),
    ]);
    setAcceptanceLatest(latest.status === 'fulfilled' ? latest.value.data || [] : []);
    setAcceptanceDatabaseRecord(database.status === 'fulfilled' ? database.value : null);
    setAcceptanceAudit(audit.status === 'fulfilled' ? audit.value.data || [] : []);
    setAcceptanceErrors([
      ...(latest.status === 'rejected' ? ['最新三筆 API 讀取失敗。'] : []),
      ...(database.status === 'rejected' ? ['單筆資料庫回讀 API 讀取失敗。'] : []),
      ...(audit.status === 'rejected' ? ['SYS_AuditLog 讀取失敗，不能把本次驗收判定為通過。'] : []),
    ]);
    setAcceptanceLoading(false);
  }, [acceptanceEnabled, record?.fg110_01]);

  useEffect(() => {
    void loadAcceptanceData();
  }, [loadAcceptanceData]);

  const handleAcceptanceAutoFill = useCallback(() => {
    if (!record) {
      message.warning('請先載入一張既有繳款單，驗收模式會複製它成為尚未送出的新草稿。');
      return;
    }
    loadAcceptanceDraft(record);
    startAdd();
    message.success('已依目前繳款單建立未送出的驗收草稿；仍須按正式儲存按鈕才會呼叫 PWG201 API。');
  }, [loadAcceptanceDraft, record, startAdd]);

  const H = (fieldId: string) => (
    <AcceptanceFieldHint
      manifest={pwg201f1AcceptanceManifest}
      fieldId={fieldId}
      documentKey={record?.fg110_01}
    />
  );

  const tabItems = [
    {
      key: 'details',
      label: `貨款明細 (${details.length})`,
      children: (
        <DetailsTab details={details} isEditing={isEditing} onChange={setDetails} />
      ),
    },
    {
      key: 'writeoff_adv',
      label: `沖預收款 (${writeoffAdv.length})`,
      children: (
        <WriteoffAdvTab rows={writeoffAdv} isEditing={isEditing} onChange={setWriteoffAdv} />
      ),
    },
    {
      key: 'writeoff_borr',
      label: `沖暫借款 (${writeoffBorr.length})`,
      children: (
        <WriteoffBorrTab rows={writeoffBorr} isEditing={isEditing} onChange={setWriteoffBorr} />
      ),
    },
    {
      key: 'writeoff_adv2',
      label: `沖代墊款 (${writeoffAdv2.length})`,
      children: (
        <WriteoffAdv2Tab rows={writeoffAdv2} isEditing={isEditing} onChange={setWriteoffAdv2} />
      ),
    },
    {
      key: 'alloc_fee',
      label: `分攤費用 (${allocFee.length})`,
      children: (
        <AllocFeeTab rows={allocFee} isEditing={isEditing} onChange={setAllocFee} />
      ),
    },
  ];

  return (
    <div className="pwg201-form">
      <AcceptanceToolbar
        manifest={pwg201f1AcceptanceManifest}
        onAutoFill={handleAcceptanceAutoFill}
        documentKey={record?.fg110_01}
        autoFillDisabled={!record || isEditing}
        autoFillReason={!record
          ? '請先載入一張可作為範例的既有繳款單。'
          : isEditing
            ? '請先完成或取消目前編輯。'
            : '複製目前單據為未送出的新草稿，正式儲存仍走原 PWG201 API。'}
      />
      <Spin spinning={loading}>
        {/* Header */}
        <div className="pwg201-header">
          <h2>繳款單（收款建檔）</h2>
          {record && <span className="pwg201-id">{record.fg110_01}</span>}
        </div>

        {/* Master Fields */}
        <div className="pwg201-master">
          <div className="pwg201-row">
            <label data-contract-field="fg110_01">繳款單號</label>
            <Input value={getField('fg110_01')} disabled style={{ width: 120 }} />{H('fg110_01')}

            <label data-contract-field="fg110_11">繳款日期</label>
            <DatePicker
              value={formData.fg110_11 ? dayjs(formData.fg110_11) : null}
              disabled={!isEditing}
              onChange={d => setField('fg110_11', d ? d.format('YYYY-MM-DD') : null)}
              style={{ width: 140 }}
            />{H('fg110_11')}

            <label data-contract-field="fg110_17">繳款類別</label>
            <Select
              value={getField('fg110_17')}
              disabled={!isEditing}
              options={PAY_TYPE_OPTIONS}
              onChange={v => setField('fg110_17', v)}
              style={{ width: 120 }}
            />{H('fg110_17')}
          </div>

          <div className="pwg201-row">
            <label data-contract-field="fg110_26">公司代號</label>
            <LookupInput
              lookupType="company"
              value={getField('fg110_26')}
              onChange={v => setField('fg110_26', v)}
              onCodeConfirm={v => setField('fg110_26', v)}
              disabled={!isEditing}
              maxLength={12}
              style={{ width: 120 }}
            />{H('fg110_26')}

            <label data-contract-field="fg110_19">部門代號</label>
            <LookupInput
              lookupType="department"
              value={getField('fg110_19')}
              onChange={v => setField('fg110_19', v)}
              onCodeConfirm={v => setField('fg110_19', v)}
              disabled={!isEditing}
              maxLength={4}
              style={{ width: 80 }}
            />{H('fg110_19')}

            <label data-contract-field="fg110_14">狀態</label>
            <Input value={getField('fg110_14')} disabled style={{ width: 60 }} />{H('fg110_14')}

            <label data-contract-field="fg110_80">核准旗標</label>
            <Input value={getField('fg110_80')} disabled style={{ width: 60 }} />{H('fg110_80')}
          </div>

          <div className="pwg201-row">
            <label data-contract-field="fg110_25">幣別</label>
            <LookupInput
              lookupType="currency"
              value={getField('fg110_25')}
              onChange={v => setField('fg110_25', v)}
              onCodeConfirm={v => setField('fg110_25', v)}
              disabled={!isEditing}
              maxLength={3}
              style={{ width: 80 }}
            />{H('fg110_25')}

            <label data-contract-field="fg110_24">水單匯率</label>
            <InputNumber
              value={formData.fg110_24}
              disabled={!isEditing}
              precision={4}
              onChange={v => setField('fg110_24', v ?? 1)}
              style={{ width: 110 }}
            />{H('fg110_24')}

            <label data-contract-field="fg110_27">匯入款單號</label>
            <input type="text"
              value={getField('fg110_27') || ''}
              disabled={!isEditing}
              onChange={e => setField('fg110_27', e.target.value)}
              style={{ width: 120 }} data-contract-field="fg110_27"
            />{H('fg110_27')}

            <label data-contract-field="fg110_28">票款別</label>
            <Select
              value={getField('fg110_28')}
              disabled={!isEditing}
              options={PAY_METHOD_OPTIONS}
              onChange={v => setField('fg110_28', v)}
              style={{ width: 120 }}
            />{H('fg110_28')}

            <label data-contract-field="fg110_29">款項別</label>
            <Select
              value={getField('fg110_29')}
              disabled={!isEditing}
              options={ITEM_CAT_OPTIONS}
              onChange={v => setField('fg110_29', v)}
              style={{ width: 120 }}
            />{H('fg110_29')}
          </div>

          <div className="pwg201-row">
            <label data-contract-field="fg110_12">原幣繳款總額</label>
            <InputNumber value={formData.fg110_12} disabled precision={0} style={{ width: 120 }} />{H('fg110_12')}

            <label data-contract-field="fg110_13">本幣繳款總額</label>
            <InputNumber value={formData.fg110_13} disabled precision={2} style={{ width: 120 }} />{H('fg110_13')}

            <label data-contract-field="fg110_16">分攤金額</label>
            <InputNumber
              value={formData.fg110_16}
              disabled={!isEditing}
              precision={2}
              onChange={v => setField('fg110_16', v ?? 0)}
              style={{ width: 120 }}
            />{H('fg110_16')}
          </div>

          <div className="pwg201-row">
            <label data-contract-field="fg110_20">備註</label>
            <Input.TextArea
              value={getField('fg110_20')}
              disabled={!isEditing}
              rows={2}
              onChange={e => setField('fg110_20', e.target.value)}
              style={{ width: 400 }}
            />{H('fg110_20')}
          </div>

          <div className="pwg201-row">
            <label data-contract-field="fg110_30">預收款類別</label>
            <Radio.Group
              value={formData.fg110_30}
              disabled={!isEditing}
              onChange={e => setField('fg110_30', e.target.value)}
            >
              {SC_NO_TYPE_OPTIONS.map(opt => (
                <Radio key={opt.value} value={opt.value}>{opt.label}</Radio>
              ))}
            </Radio.Group>{H('fg110_30')}

            <label data-contract-field="fg110_32">建單日期</label>
            <DatePicker
              value={formData.fg110_32 ? dayjs(formData.fg110_32) : null}
              disabled
              style={{ width: 140 }}
            />{H('fg110_32')}

            <label data-contract-field="fg110_92">修改日期</label>
            <DatePicker
              value={formData.fg110_92 ? dayjs(formData.fg110_92) : null}
              disabled
              style={{ width: 140 }}
            />{H('fg110_92')}
          </div>

          {validationErrors.length > 0 && (
            <div className="pwg201-errors">
              {validationErrors.map((e, i) => (
                <div key={i} className="pwg201-error">
                  {e.field}: {e.message}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sub-table Tabs */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          className="pwg201-tabs"
        />
      </Spin>

      {/* SharedActionBar */}
      <SharedActionBar
        mode={mode === 'browse' ? 'browse' : mode} isEditing={isEditing} hasRecord={!!record}
        currentIdx={currentIdx} recordCount={allIds.length}
        onNavFirst={navFirst} onNavPrev={navPrev}
        onNavNext={navNext} onNavLast={navLast}
        onAdd={handleAdd} onModify={handleEdit} onDelete={handleDeleteClick}
        onSave={handleSaveClick} onCancel={handleRevert}
        onExit={() => navigate(-1)}
        canView={permissions.canView}
        canCreate={permissions.canCreate} canEdit={permissions.canEdit}
        canDelete={permissions.canDelete} canApprove={permissions.canApprove}
        acceptanceActionIds={{
          create: 'PWG201-ACT-CREATE',
          update: 'PWG201-ACT-UPDATE',
          delete: 'PWG201-ACT-DELETE',
          get: 'PWG201-ACT-GET',
          list: 'PWG201-ACT-LIST',
        }}
      />

      {/* Approval History Modal */}
      <ApprovalHistoryModal
        visible={showHistory}
        docNo={record?.fg110_01 || ''}
        onClose={() => setShowHistory(false)}
      />

      <AcceptanceDataPanel
        manifest={pwg201f1AcceptanceManifest}
        documentKey={record?.fg110_01}
        snapshots={{
          screenDraft: {
            master: formData,
            details,
            writeoff_adv: writeoffAdv,
            writeoff_borr: writeoffBorr,
            writeoff_adv2: writeoffAdv2,
            alloc_fee: allocFee,
          },
          lastApiResponse: lastApiResponse ? {
            master: lastApiResponse,
            details: lastApiResponse.details || [],
            writeoff_adv: lastApiResponse.writeoff_adv || [],
            writeoff_borr: lastApiResponse.writeoff_borr || [],
            writeoff_adv2: lastApiResponse.writeoff_adv2 || [],
            alloc_fee: lastApiResponse.alloc_fee || [],
          } : null,
          databaseRecord: acceptanceDatabaseRecord ? {
            master: acceptanceDatabaseRecord,
            details: acceptanceDatabaseRecord.details || [],
            writeoff_adv: acceptanceDatabaseRecord.writeoff_adv || [],
            writeoff_borr: acceptanceDatabaseRecord.writeoff_borr || [],
            writeoff_adv2: acceptanceDatabaseRecord.writeoff_adv2 || [],
            alloc_fee: acceptanceDatabaseRecord.alloc_fee || [],
          } : null,
        }}
        latestRecords={acceptanceLatest}
        history={[]}
        auditEntries={acceptanceAudit}
        loading={acceptanceLoading || loading}
        onRefresh={() => { void loadAcceptanceData(); }}
        observationErrors={acceptanceErrors}
      />
    </div>
  );
};

export default PWG201Form;
